// The whole frontend/jaaluu app is one shared static build served to all 9
// language subdomains (see frontend/shared/docker/default.conf) - nginx's
// `try_files $uri.html` picks the right prerendered file purely by request
// path, so every absolute route path AND every route name must be globally
// unique across the entire route table, not just "unique per UI language".
// At 81 hand-picked hub slugs a collision never happened; at 576+ generated
// spoke slugs (see @jaaluu/router/spokeRoutes.ts) a slug-templating bug is
// the realistic failure mode, and Vue Router 4 silently overwrites on a
// duplicate route `name` rather than erroring - so this check is not
// optional at this scale. Called from router/index.ts at module load
// (throws immediately in dev and at build time) and mirrored in
// scripts/prerender-html-helpers.mjs (which is what actually writes files to
// disk and would otherwise silently overwrite one page's prerendered HTML
// with another's).

type RouteLike = { path?: string; name?: string | symbol; children?: RouteLike[] };

export const assertUniqueRoutes = (routes: RouteLike[]): void => {
  const absolutePaths = new Map<string, string>();
  const names = new Map<string, string>();

  const visit = (list: RouteLike[]) => {
    for (const route of list) {
      const label = String(route.name ?? route.path ?? '(unnamed route)');

      if (route.path && route.path.startsWith('/')) {
        const previous = absolutePaths.get(route.path);
        if (previous) {
          throw new Error(
            `Duplicate route path "${route.path}" (declared by "${previous}" and "${label}")`
          );
        }
        absolutePaths.set(route.path, label);
      }

      if (route.name) {
        const key = String(route.name);
        const previous = names.get(key);
        if (previous) {
          throw new Error(
            `Duplicate route name "${key}" (paths "${previous}" and "${route.path ?? '(no path)'}")`
          );
        }
        names.set(key, String(route.path ?? '(no path)'));
      }

      if (route.children) visit(route.children);
    }
  };

  visit(routes);
};
